import toneStyles from './tones.module.css'

/**
 * Los tonos del eje de acción, y su clase.
 *
 * Vive junto a `tones.module.css` porque es la otra mitad del mismo hecho: el CSS declara las
 * clases y esto dice cómo se piden. Lo leen el botón, el chip y los dos chips interactivos.
 *
 * **Dos tipos con distinta cardinalidad, no uno compartido.** `ActionTone` son los tres tonos que
 * puede pedir una acción —`Button`, `ChipButton`, `ChipLink`—: el azul de la acción principal, el
 * acromático, y el rojo de lo destructivo. `ChipTone` les agrega `success` y `warning`, porque un
 * chip es inerte y puede mostrar un estado que ninguna acción necesita.
 *
 * **`warning` está acá y no en `ActionTone`, y la asimetría es la decisión.** Un aviso no es una
 * acción: no hay nada que «advertir» apretando un botón, y el ámbar en un control accionable se lee
 * como un estado del control y no del dato. El chip sí: es inerte, solo muestra. Por eso `warning`
 * entra al eje de tono sin par sólido —ver `tones.module.css`— y sigue estando además en
 * `FeedbackTone` (`styles/feedback-tone.ts`), que es el eje del `Callout`, el `Toast` y el
 * `Progress`. Los dos ejes conviven: uno tiñe un chip, el otro pinta un aviso.
 *
 * `ChipTone` es un superset estricto de `ActionTone`, así que un solo `TONE_CLASS` tipado a
 * `ChipTone` le sirve a los cuatro componentes sin casteos: indexar un `Record<ChipTone, string>`
 * con un `ActionTone` type-checkea solo.
 *
 * **Por qué un mapa y no `toneStyles[`au-tone--${tone}`]`.** Una clave armada con una plantilla no
 * la puede verificar nada: `tsc` tipa los módulos como `{ readonly [key: string]: string }`, así que
 * cualquier clave compila; el eje 6 de `audit:tokens`, que lee el texto y no ejecuta, sólo puede
 * comprobar que **alguna** clase del prefijo exista. Con eso, agregar un tono al union y olvidar su
 * regla pasa los dos filtros y sale a producción como un control sin color.
 *
 * `Record<ChipTone, string>` lo vuelve un error de compilación acá, que es donde tiene que romper. Y
 * de paso cada clase queda escrita como clave literal, o sea que el eje 6 la verifica una por una.
 */
export type ActionTone = 'primary' | 'info' | 'error'

export type ChipTone = ActionTone | 'success' | 'warning'

export const TONE_CLASS: Record<ChipTone, string> = {
  primary: toneStyles['au-tone--primary'],
  info: toneStyles['au-tone--info'],
  success: toneStyles['au-tone--success'],
  warning: toneStyles['au-tone--warning'],
  error: toneStyles['au-tone--error'],
}
